import { useState } from "react";
import { ArrowRight, Pencil, Plus, Users, X } from "lucide-react";
import {
  addTeamMember,
  moveMemberToSolo,
  removeTeamMember,
  renameTeamForEvent,
  type MyEventRegistration,
  type PastTeamSummary,
  type PersonSearchResult,
  type RegistrationMember,
} from "@/api/registrations";
import type { UpcomingEvent } from "@/api/eventDiscovery";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PaymentStatusPill } from "@/components/profile/PaymentStatusPill";
import { THINKER_MARK, THINKER_LABEL } from "@/lib/thinker";
import { AddMemberSearch } from "./AddMemberSearch";
import { CancelRegistrationLink } from "./CancelRegistrationLink";
import { SwitchToTeamPanel } from "./SwitchToTeamPanel";
import { TeamNameEditor } from "./TeamNameEditor";
import { usePanelState } from "./panelState";
import { formatName, type OnAction } from "./types";

interface Props {
  event: UpcomingEvent;
  personId: number;
  registration: MyEventRegistration;
  pastTeams: PastTeamSummary[];
  busy: boolean;
  onAction: OnAction;
  /** Refresh and collapse the panel after the registration is cancelled. */
  onCancelled: () => void;
}

// Member actions here commit immediately through onAction (which reloads the
// registration on success), so the view always renders live server state — no
// staged edits, no save bar.
export function RegisteredView({
  event,
  personId,
  registration,
  pastTeams,
  busy,
  onAction,
  onCancelled,
}: Props) {
  const isTeam = registration.category === "team";
  const canEdit = registration.can_edit;
  const [panel, dispatch] = usePanelState();
  const { editingName, showSwitchToTeam, showAddSearch } = panel;

  const [removeTarget, setRemoveTarget] = useState<RegistrationMember | null>(
    null,
  );
  const [soloTarget, setSoloTarget] = useState<RegistrationMember | null>(null);

  const members = registration.members;
  const teamName = registration.team_name ?? "";

  const handleRename = async (name: string) => {
    dispatch({ type: "stop_edit_name" });
    if (name === registration.team_name) return;
    await onAction(
      () => renameTeamForEvent(event.event_id, name),
      "Team renamed",
      "Couldn't rename team",
      "Please try again.",
    );
  };

  const handleAddMember = async (p: PersonSearchResult) => {
    const ok = await onAction(
      () => addTeamMember(event.event_id, p.person_id),
      `${formatName(p)} added`,
      "Couldn't add player",
      "Please try again.",
    );
    if (ok) dispatch({ type: "set_show_add_search", value: false });
  };

  const handleRemoveMember = (member: RegistrationMember) =>
    onAction(
      () => removeTeamMember(event.event_id, member.person_id),
      `${formatName(member)} removed`,
      "Couldn't remove member",
      "Please try again.",
    );

  const handleMoveToSolo = (member: RegistrationMember) =>
    onAction(
      () => moveMemberToSolo(event.event_id, member.person_id),
      `${formatName(member)} moved to solo`,
      "Couldn't move to solo",
      "Please try again.",
    );

  // Paid members get a confirmation before removal; everyone else goes straight.
  const handleRemoveClick = (member: RegistrationMember) => {
    if (member.payment_status === "paid") {
      setRemoveTarget(member);
    } else {
      void handleRemoveMember(member);
    }
  };

  const showMemberActions = isTeam && canEdit;

  return (
    <div className="space-y-4">
      {/* Header: team name (editable) or Locked badge. Solo has no header row —
          its actions render directly below. */}
      {(isTeam || !canEdit) && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
            {!isTeam ? (
              <span className="text-muted-foreground">Solo</span>
            ) : editingName && canEdit ? (
              <TeamNameEditor
                initial={teamName}
                onCommit={handleRename}
                onCancel={() => dispatch({ type: "stop_edit_name" })}
              />
            ) : (
              <button
                type="button"
                className="group inline-flex min-w-0 items-center gap-1.5"
                onClick={() => canEdit && dispatch({ type: "start_edit_name" })}
                disabled={!canEdit}
              >
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  Team · {teamName || "(unnamed)"}
                </span>
                {canEdit && (
                  <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" />
                )}
              </button>
            )}
          </div>
          {!canEdit && (
            <span className="text-xs text-muted-foreground shrink-0">
              Locked
            </span>
          )}
        </div>
      )}

      {/* Solo: Switch to team + Cancel render directly (no Manage button). */}
      {!isTeam && canEdit && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
          {!showSwitchToTeam && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                dispatch({ type: "set_show_switch_to_team", value: true })
              }
            >
              <Users className="h-4 w-4 mr-2" />
              Switch to team
            </Button>
          )}
          <CancelRegistrationLink
            eventId={event.event_id}
            isTeam={false}
            onCancelled={onCancelled}
            className="ml-auto"
          />
        </div>
      )}

      {/* Member list (team only) */}
      {isTeam && members.length > 0 && (
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Members ({members.length})
          </Label>
          <ul
            className={
              members.length > 1
                ? "mt-2 divide-y rounded-md border"
                : "mt-2 rounded-md border"
            }
          >
            {members.map((m) => {
              const isMe = m.person_id === personId;
              return (
                <li
                  key={m.person_id}
                  className="flex flex-col gap-2 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="font-medium truncate">
                      {formatName(m)}
                      {m.is_thinker && (
                        <span
                          className="ml-0.5 text-muted-foreground"
                          title={THINKER_LABEL}
                        >
                          {THINKER_MARK}
                        </span>
                      )}
                      {isMe && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          (you)
                        </span>
                      )}
                    </span>
                    {/* is_free → Free pill; paid → Paid; else Unpaid. */}
                    <PaymentStatusPill
                      status={
                        m.is_free ? "free" : (m.payment_status ?? "unpaid")
                      }
                    />
                  </div>
                  {showMemberActions && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveClick(m)}
                        disabled={busy}
                        data-testid={`remove-member-${m.person_id}`}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setSoloTarget(m)}
                        disabled={busy}
                        data-testid={`to-solo-${m.person_id}`}
                      >
                        <ArrowRight className="h-3.5 w-3.5 mr-1" />
                        To Solo
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {members.some((m) => m.is_thinker) && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              <span aria-hidden="true">{THINKER_MARK}</span> {THINKER_LABEL}
            </p>
          )}
        </div>
      )}

      {/* Team footer: Add player (left) + Cancel team registration (right). */}
      {isTeam &&
        canEdit &&
        (showAddSearch ? (
          <AddMemberSearch
            event={event}
            excludeIds={members.map((m) => m.person_id)}
            onAdd={handleAddMember}
            onCancel={() =>
              dispatch({ type: "set_show_add_search", value: false })
            }
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                dispatch({ type: "set_show_add_search", value: true })
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              Add player
            </Button>
            <CancelRegistrationLink
              eventId={event.event_id}
              isTeam
              onCancelled={onCancelled}
            />
          </div>
        ))}

      {/* Switch-to-team panel (solo only) */}
      {!isTeam && canEdit && showSwitchToTeam && (
        <div className="rounded-md border bg-muted/30 p-3">
          <SwitchToTeamPanel
            event={event}
            pastTeams={pastTeams}
            busy={busy}
            onAction={onAction}
            onCancel={() =>
              dispatch({ type: "set_show_switch_to_team", value: false })
            }
          />
        </div>
      )}

      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          The editing window has closed. Contact an organiser for changes.
        </p>
      )}

      {/* Paid-member removal warning */}
      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove a paid player?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && formatName(removeTarget)} has paid for this
              event. Removing them keeps their payment on record but takes them
              out of the team. This can't be undone here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (removeTarget) void handleRemoveMember(removeTarget);
                setRemoveTarget(null);
              }}
            >
              Remove anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* To Solo confirmation */}
      <Dialog
        open={!!soloTarget}
        onOpenChange={(open) => {
          if (!open) setSoloTarget(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move to solo?</DialogTitle>
            <DialogDescription>
              Move {soloTarget && formatName(soloTarget)} to their own solo
              registration?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoloTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (soloTarget) void handleMoveToSolo(soloTarget);
                setSoloTarget(null);
              }}
            >
              Move to solo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
